import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { TextField } from "@package/subframe/components/TextField";
import { FeatherX } from "@subframe/core";
import axios from "axios";
import { useState } from "react";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { sanitizePin } from "@/features/device-setup/pin-login-api";
import { useBulkSetTemporaryPinsCommandControllerBulkSetPins } from "@/generated/users/users";

const PIN_LENGTH = 4;
const FORBIDDEN =
  "You don't have permission to set a PIN for some of the selected staff.";
const GENERIC_ERROR = "Something went wrong. Please try again.";

interface BulkSetPinDialogProps {
  userIds: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after the PIN was set for every selected user. */
  onSuccess: () => void;
}

/**
 * Migration helper: set the same temporary PIN for all selected
 * staff at once. Each of them enters it on their next login and must then
 * choose their own PIN. All-or-nothing on the server.
 */
export function BulkSetPinDialog({
  userIds,
  open,
  onOpenChange,
  onSuccess,
}: BulkSetPinDialogProps) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const bulkSetPins = useBulkSetTemporaryPinsCommandControllerBulkSetPins();
  const isSaving = bulkSetPins.isPending;

  function close() {
    if (isSaving) return;
    setPin("");
    setError(null);
    onOpenChange(false);
  }

  async function handleSave() {
    if (pin.length !== PIN_LENGTH || userIds.length === 0) return;
    setError(null);
    try {
      await bulkSetPins.mutateAsync({ data: { userIds, temporaryPin: pin } });
      toast.success(
        `Temporary PIN set for ${userIds.length} staff member${userIds.length === 1 ? "" : "s"}.`,
      );
      setPin("");
      onOpenChange(false);
      onSuccess();
    } catch (err) {
      setError(
        axios.isAxiosError(err) && err.response?.status === 403
          ? FORBIDDEN
          : GENERIC_ERROR,
      );
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
    >
      <DialogContent showCloseButton={false} className="max-w-md p-0">
        <DialogTitle className="sr-only">Set temporary PIN</DialogTitle>

        <div className="flex items-start justify-between border-b border-solid border-secondary-beige-200 px-6 py-4">
          <div className="flex flex-col items-start gap-1">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Set temporary PIN
            </span>
            <span className="text-body font-body text-subtext-color">
              Set the same temporary 4-digit PIN for the {userIds.length}{" "}
              selected staff member{userIds.length === 1 ? "" : "s"}. Share it
              with them — each will choose their own PIN on their first login.
            </span>
          </div>
          <IconButton
            variant="neutral-tertiary"
            size="medium"
            icon={<FeatherX />}
            disabled={isSaving}
            onClick={close}
          />
        </div>

        <form
          className="flex flex-col gap-6 px-6 py-6"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSave();
          }}
        >
          <TextField
            className="h-auto w-full"
            size="large"
            label="Temporary PIN"
          >
            <TextField.Input
              inputMode="numeric"
              autoComplete="off"
              autoFocus
              placeholder="4-digit temporary PIN"
              value={pin}
              disabled={isSaving}
              onChange={(e) => {
                setPin(sanitizePin(e.target.value, PIN_LENGTH));
                setError(null);
              }}
            />
          </TextField>

          {error && (
            <span className="text-body font-body text-error-700">{error}</span>
          )}

          <div className="flex items-center justify-end gap-3">
            <Button
              type="button"
              variant="neutral-primary"
              size="large"
              disabled={isSaving}
              onClick={close}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="brand-primary"
              size="large"
              disabled={isSaving || pin.length !== PIN_LENGTH}
              loading={isSaving}
            >
              Set PIN
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
